/**
 * Product Page Scratch Card Overlay
 * Interactive 4-stage gamified discount popup with floating teaser badge
 */
class ProductScratchOverlay extends HTMLElement {
  constructor() {
    super();

    // Prevent running on Shopify challenge page
    if (window.location.pathname === '/challenge') {
      return;
    }

    this.cookieSeenName = 'product-scratch-overlay-seen';
    this.cookieClaimedName = 'product-scratch-overlay-claimed';
    this.storageTeaserDismissed = 'product-scratch-teaser-dismissed';

    this.root = this.querySelector('.scratch-overlay-root');
    this.closeBtn = this.querySelector('.scratch-overlay-close');
    this.backdrop = this.querySelector('.scratch-overlay-backdrop');
    this.teaserBadge = this.querySelector('.scratch-teaser-badge');
    this.teaserCloseBtn = this.querySelector('.scratch-teaser-close');
    
    // Stages
    this.stage1 = this.querySelector('[data-stage="1"]');
    this.stage2 = this.querySelector('[data-stage="2"]');
    this.stage3 = this.querySelector('[data-stage="3"]');
    this.stage4 = this.querySelector('[data-stage="4"]');

    // Controls
    this.claimBtn = this.querySelector('[data-action="claim"]');
    this.emailForm = this.querySelector('.scratch-form');
    this.emailInput = this.querySelector('.scratch-input');
    this.continueBtn = this.querySelector('[data-action="continue"]');
    this.errorMsg = this.querySelector('.scratch-error-message');
    this.couponBox = this.querySelector('.scratch-coupon-box');
    this.copyFeedback = this.querySelector('.scratch-copy-feedback');
    this.shopNowBtn = this.querySelector('[data-action="shop-now"]');

    // Canvas
    this.canvas = this.querySelector('.scratch-canvas');
    this.canvasWrapper = this.querySelector('.scratch-card-wrapper');
    this.ctx = null;
    this.isDrawing = false;
    this.hasRevealed = false;
    this.scratchPointsCount = 0;

    // Settings
    this.displayMode = this.dataset.displayMode || 'test';
    this.targetPages = this.dataset.targetPages || 'product_only';
    this.delay = parseInt(this.dataset.delay || 0, 10);
    this.expiryDays = parseInt(this.dataset.expiry || 7, 10);
    this.discountCode = this.dataset.code || 'WELCOME15';
    this.scratchText = this.dataset.scratchText || 'SCRATCH HERE';
    this.enableTeaser = this.dataset.enableTeaser !== 'false';

    // Traffic source gate
    this.hideForPaid = this.dataset.hidePaid !== 'false';
    this.paidWindowHours = parseInt(this.dataset.paidWindow || 24, 10) || 24;
    this.extraPaidMarkers = (this.dataset.paidParams || '')
      .split(',')
      .map((entry) => entry.trim().toLowerCase())
      .filter(Boolean);

    this.bindEvents();
    this.init();
  }

  bindEvents() {
    // Overlay Close
    this.closeBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      this.close();
    });

    this.backdrop?.addEventListener('click', (e) => {
      e.preventDefault();
      this.close();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.root?.classList.contains('is-visible')) {
        this.close();
      }
    });

    // Teaser Click (Re-open overlay)
    this.teaserBadge?.addEventListener('click', (e) => {
      if (e.target.closest('.scratch-teaser-close')) return;
      this.hideTeaser();
      this.open();
    });

    // Teaser Close ("✕")
    this.teaserCloseBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.hideTeaser();
      if (this.displayMode !== 'test') {
        try { localStorage.setItem(this.storageTeaserDismissed, 'true'); } catch (err) {}
      }
    });

    // Stage 2: Claim Button
    this.claimBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      this.goToStage(3);
      setTimeout(() => this.emailInput?.focus(), 250);
    });

    // Stage 3: Email Form Submit
    this.emailForm?.addEventListener('submit', (e) => this.handleEmailSubmit(e));
    this.continueBtn?.addEventListener('click', (e) => this.handleEmailSubmit(e));

    // Stage 4: Copy Code
    this.couponBox?.addEventListener('click', () => this.copyCode());

    // Stage 4: Shop Now (close overlay, apply discount silently, user continues browsing on current product page)
    this.shopNowBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      this.markAsClaimed();
      this.applyDiscount(this.discountCode);
      this.closeModalOnly();
    });

    // Shopify Theme Editor Support
    if (window.Shopify && Shopify.designMode) {
      document.addEventListener('shopify:section:select', (event) => {
        if (event.target === this || this.contains(event.target)) {
          this.open();
        }
      });
      document.addEventListener('shopify:section:deselect', (event) => {
        if (event.target === this || this.contains(event.target)) {
          this.closeModalOnly();
        }
      });
    }
  }

  init() {
    // Traffic source gate: the scratch offer is for cold traffic only. Returning
    // here suppresses both the overlay and the teaser badge.
    if (!(window.Shopify && Shopify.designMode) && this.isPaidTraffic()) {
      return;
    }

    // Target Pages Check
    if (this.targetPages === 'product_only') {
      const isProductPage = window.location.pathname.includes('/products/') || 
                            document.body.classList.contains('template-product') ||
                            document.querySelector('.product__info-container, .main-for-product-page');
      if (!isProductPage && !Shopify.designMode) {
        return;
      }
    }

    // Check if device already claimed
    const isClaimed = this.getCookie(this.cookieClaimedName) || this.getStorageItem(this.cookieClaimedName);
    if (this.displayMode !== 'test' && isClaimed && !Shopify.designMode) {
      return;
    }

    // Check if device previously closed popup in this session/expiry
    const isSeen = this.getCookie(this.cookieSeenName);
    const isTeaserDismissed = this.getStorageItem(this.storageTeaserDismissed);

    if (this.displayMode !== 'test' && isSeen && !Shopify.designMode) {
      if (this.enableTeaser && !isTeaserDismissed) {
        this.showTeaser();
      }
      return;
    }

    // Launch immediately on page ready
    const launch = () => {
      const delayMs = Math.max(0, this.delay * 1000);
      if (delayMs === 0) {
        this.open();
      } else {
        setTimeout(() => {
          const claimedNow = this.getCookie(this.cookieClaimedName) || this.getStorageItem(this.cookieClaimedName);
          if (this.displayMode !== 'test' && claimedNow && !Shopify.designMode) return;
          this.open();
        }, delayMs);
      }
    };

    if (document.readyState === 'interactive' || document.readyState === 'complete') {
      launch();
    } else {
      document.addEventListener('DOMContentLoaded', launch);
    }
  }

  open() {
    this.hideTeaser();
    this.goToStage(1);
    this.root?.classList.add('is-visible');
    document.body.classList.add('overflow-hidden');
    document.documentElement.classList.add('overflow-hidden');
    this.initScratchCanvas();
  }

  close() {
    this.closeModalOnly();

    // If not claimed yet, show the floating teaser badge
    const isClaimed = this.getCookie(this.cookieClaimedName) || this.getStorageItem(this.cookieClaimedName);
    if (!isClaimed && this.enableTeaser) {
      const isTeaserDismissed = this.getStorageItem(this.storageTeaserDismissed);
      if (!isTeaserDismissed || this.displayMode === 'test') {
        this.showTeaser();
      }
    }

    if (this.displayMode !== 'test') {
      this.setCookie(this.cookieSeenName, 'true', this.expiryDays);
    }
  }

  closeModalOnly() {
    this.root?.classList.remove('is-visible');
    document.body.classList.remove('overflow-hidden');
    document.documentElement.classList.remove('overflow-hidden');
  }

  showTeaser() {
    if (this.teaserBadge) {
      this.teaserBadge.classList.add('is-visible');
    }
  }

  hideTeaser() {
    if (this.teaserBadge) {
      this.teaserBadge.classList.remove('is-visible');
    }
  }

  markAsClaimed() {
    this.setCookie(this.cookieClaimedName, 'true', this.expiryDays);
    try { localStorage.setItem(this.cookieClaimedName, 'true'); } catch (err) {}
    this.hideTeaser();
  }

  goToStage(stageNum) {
    [this.stage1, this.stage2, this.stage3, this.stage4].forEach((stage, idx) => {
      if (stage) {
        if (idx + 1 === stageNum) {
          stage.classList.add('is-active');
        } else {
          stage.classList.remove('is-active');
        }
      }
    });
  }

  /* ==========================================================================
     HTML5 CANVAS SCRATCH ENGINE
     ========================================================================== */

  initScratchCanvas() {
    if (!this.canvas || !this.canvasWrapper) return;

    this.ctx = this.canvas.getContext('2d', { willReadFrequently: true });
    this.hasRevealed = false;
    this.scratchPointsCount = 0;
    this.canvas.style.opacity = '1';

    const rect = this.canvasWrapper.getBoundingClientRect();
    const width = rect.width || 320;
    const height = rect.height || 180;
    const dpr = window.devicePixelRatio || 1;

    this.canvas.width = width * dpr;
    this.canvas.height = height * dpr;
    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;

    this.ctx.scale(dpr, dpr);
    this.drawGoldSurface(width, height);
    this.setupCanvasEvents();
  }

  drawGoldSurface(width, height) {
    const ctx = this.ctx;
    ctx.save();
    ctx.globalCompositeOperation = 'source-over';

    // Metallic Gold Gradient
    const grad = ctx.createLinearGradient(0, 0, width, height);
    grad.addColorStop(0.00, '#C49746');
    grad.addColorStop(0.18, '#E5BE6B');
    grad.addColorStop(0.35, '#F9E397');
    grad.addColorStop(0.52, '#D2A14D');
    grad.addColorStop(0.72, '#E8C576');
    grad.addColorStop(0.88, '#C29341');
    grad.addColorStop(1.00, '#E0B862');

    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, width, height);

    // Subtle brushed texture
    ctx.fillStyle = 'rgba(255, 255, 255, 0.08)';
    for (let i = 0; i < height; i += 3) {
      ctx.fillRect(0, i, width, 1.2);
    }

    // Centered "SCRATCH HERE" text
    ctx.font = '700 24px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.letterSpacing = '1px';

    // Embossed shadow
    ctx.fillStyle = 'rgba(120, 80, 20, 0.25)';
    ctx.fillText(this.scratchText, width / 2 + 1, height / 2 + 1);

    // Main text color
    ctx.fillStyle = 'rgba(255, 255, 255, 0.65)';
    ctx.fillText(this.scratchText, width / 2, height / 2);

    ctx.restore();
  }

  setupCanvasEvents() {
    const getPos = (e) => {
      const rect = this.canvas.getBoundingClientRect();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      return {
        x: clientX - rect.left,
        y: clientY - rect.top
      };
    };

    const startScratch = (e) => {
      this.isDrawing = true;
      const pos = getPos(e);
      this.scratch(pos.x, pos.y);
    };

    const moveScratch = (e) => {
      if (!this.isDrawing) return;
      e.preventDefault();
      const pos = getPos(e);
      this.scratch(pos.x, pos.y);
    };

    const stopScratch = () => {
      if (!this.isDrawing) return;
      this.isDrawing = false;
      this.checkScratchProgress();
    };

    this.canvas.onpointerdown = startScratch;
    this.canvas.onpointermove = moveScratch;
    window.addEventListener('pointerup', stopScratch);
    window.addEventListener('pointercancel', stopScratch);
  }

  scratch(x, y) {
    if (!this.ctx || this.hasRevealed) return;

    this.ctx.save();
    this.ctx.globalCompositeOperation = 'destination-out';
    this.ctx.beginPath();
    this.ctx.arc(x, y, 22, 0, Math.PI * 2, false);
    this.ctx.fill();
    this.ctx.restore();

    this.scratchPointsCount++;
    if (this.scratchPointsCount % 6 === 0) {
      this.checkScratchProgress();
    }
  }

  checkScratchProgress() {
    if (this.hasRevealed) return;

    if (this.scratchPointsCount >= 18) {
      this.revealPrize();
      return;
    }

    try {
      const dpr = window.devicePixelRatio || 1;
      const w = this.canvas.width;
      const h = this.canvas.height;
      const imgData = this.ctx.getImageData(0, 0, w, h);
      const pixels = imgData.data;
      let transparentCount = 0;
      const sampleStep = 32 * 4;

      for (let i = 3; i < pixels.length; i += sampleStep) {
        if (pixels[i] === 0) {
          transparentCount++;
        }
      }

      const totalSampled = pixels.length / sampleStep;
      const ratio = transparentCount / totalSampled;

      if (ratio > 0.32) {
        this.revealPrize();
      }
    } catch (err) {
      if (this.scratchPointsCount > 10) {
        this.revealPrize();
      }
    }
  }

  revealPrize() {
    if (this.hasRevealed) return;
    this.hasRevealed = true;

    if (this.canvas) {
      this.canvas.style.opacity = '0';
    }

    setTimeout(() => {
      this.goToStage(2);
    }, 400);
  }

  /* ==========================================================================
     STAGE 3: EMAIL SUBMISSION (AJAX)
     ========================================================================== */

  handleEmailSubmit(e) {
    if (e) e.preventDefault();
    if (!this.emailInput) return;

    const email = this.emailInput.value.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(email)) {
      if (this.errorMsg) {
        this.errorMsg.textContent = 'Please enter a valid email address.';
        this.errorMsg.classList.add('is-visible');
      }
      this.emailInput.focus();
      return;
    }

    if (this.errorMsg) {
      this.errorMsg.classList.remove('is-visible');
    }

    if (this.continueBtn) {
      this.continueBtn.disabled = true;
      this.continueBtn.textContent = 'Submitting...';
    }

    // Mark claimed upon email submission
    this.markAsClaimed();

    // Submit to Shopify Customers via AJAX
    const formData = new FormData();
    formData.append('form_type', 'customer');
    formData.append('utf8', '✓');
    formData.append('contact[tags]', 'newsletter, product-scratch-discount');
    formData.append('contact[email]', email);

    fetch('/contact#contact_form', {
      method: 'POST',
      body: formData,
      headers: {
        'Accept': 'text/html,application/xhtml+xml,application/xml'
      }
    })
    .then(() => {
      this.goToStage(4);
    })
    .catch(() => {
      this.goToStage(4);
    })
    .finally(() => {
      if (this.continueBtn) {
        this.continueBtn.disabled = false;
        this.continueBtn.textContent = 'Continue';
      }
    });
  }

  /* ==========================================================================
     STAGE 4: DISCOUNT & AUTO-APPLY
     ========================================================================== */

  copyCode() {
    if (!this.discountCode) return;

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(this.discountCode).then(() => {
        this.showCopyTooltip();
      });
    } else {
      const input = document.createElement('input');
      input.value = this.discountCode;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      this.showCopyTooltip();
    }
  }

  showCopyTooltip() {
    if (!this.copyFeedback) return;
    this.copyFeedback.classList.add('is-shown');
    setTimeout(() => {
      this.copyFeedback.classList.remove('is-shown');
    }, 1800);
  }

  applyDiscount(code) {
    if (!code) return;
    fetch(`/discount/${encodeURIComponent(code)}`, { method: 'GET' }).catch(() => {});
  }

  /* ==========================================================================
     TRAFFIC SOURCE
     Paid visitors already arrived on an ad's terms; stacking a surprise
     discount on top only gives margin away. The ad markers exist only on the
     landing URL, so a positive verdict is stored for the attribution window and
     re-read on every page after that.
     ========================================================================== */

  isPaidTraffic() {
    if (!this.hideForPaid) return false;

    const key = 'product-scratch-paid-traffic';

    if (this.landingIsPaid()) {
      this.setCookieHours(key, 'true', this.paidWindowHours);
      try { sessionStorage.setItem(key, 'true'); } catch (err) {}
      return true;
    }

    if (this.getCookie(key) === 'true') return true;
    try { return sessionStorage.getItem(key) === 'true'; } catch (err) { return false; }
  }

  landingIsPaid() {
    let params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (err) {
      return false;
    }

    // Click identifiers the ad networks append to every landing URL.
    const clickIds = [
      'gclid', 'gbraid', 'wbraid', 'dclid',   // Google Ads / Display
      'fbclid',                               // Meta (Facebook, Instagram)
      'ttclid',                               // TikTok
      'msclkid',                              // Microsoft / Bing
      'twclid',                               // X
      'li_fat_id',                            // LinkedIn
      'sccid',                                // Snapchat
      'epik',                                 // Pinterest
      'irclickid', 'yclid', 'rdt_cid',        // Impact, Yandex, Reddit
    ];
    if (clickIds.some((id) => params.has(id))) return true;

    // utm_medium values that only ever mean a paid placement.
    const paidMediums = [
      'cpc', 'ppc', 'cpm', 'cpa', 'cpv', 'sem',
      'paid', 'paidsocial', 'paid_social', 'paid-social', 'paid social',
      'social_paid', 'social-paid', 'paid_ads', 'paid-ads', 'ads',
      'display', 'retargeting', 'remarketing',
    ];
    const medium = (params.get('utm_medium') || '').trim().toLowerCase();
    if (medium && paidMediums.includes(medium)) return true;

    // Merchant-defined markers: "name" or "name=value".
    return this.extraPaidMarkers.some((marker) => {
      const eq = marker.indexOf('=');
      if (eq === -1) return params.has(marker);
      const name = marker.slice(0, eq);
      const wanted = marker.slice(eq + 1);
      const actual = params.get(name);
      return actual !== null && actual.trim().toLowerCase() === wanted;
    });
  }

  setCookieHours(name, value, hours) {
    const maxAge = Math.round(hours * 60 * 60);
    document.cookie = `${name}=${value}; max-age=${maxAge}; path=/; SameSite=Lax`;
  }

  /* ==========================================================================
     STORAGE & COOKIE UTILS
     ========================================================================== */

  getCookie(name) {
    const match = document.cookie.match(new RegExp('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)'));
    return match ? match[2] : null;
  }

  setCookie(name, value, days) {
    const maxAge = days * 24 * 60 * 60;
    document.cookie = `${name}=${value}; max-age=${maxAge}; path=/; SameSite=Lax`;
  }

  getStorageItem(key) {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }
}

if (!customElements.get('product-scratch-overlay')) {
  customElements.define('product-scratch-overlay', ProductScratchOverlay);
}
